import React, { useEffect, useState } from "react";
import { Download, RefreshCw, Filter, Shield, Search } from "lucide-react";
import { useAuth } from "../../../context/AuthProvider";
import { toast } from "react-toastify";
import { archivalApprovedAndReject } from "../../../api/archivalReqestApis";
import {
  exportArchivalReportCSV,
  exportArchivalReportExcel,
  exportArchivalReportPDF,
  getAllControllers,
  getAllUsernames,
  getArchivalReport,
} from "../../../api/reportAPI";
import logger from "../../../utils/logger";
import DateInput from "react-calendar-ui-kit";
import { formatDisplayDate, toISOForDB } from "../../../utils/Date";
import ArchivalTableRow from "./ArchivalTableRow";
import type { ArchivalRequest } from "./ArchivalRequest.types";
import ArchivalDetailsModal from "./ArchivalDetailsModal";
import {
  renderTableMessage,
  renderButtonIcon,
  getSummaryCards,
} from "./reportUtils";
import Pagination from "../../../utils/Pagination";
const ArchivalReport: React.FC = () => {
  const { user } = useAuth();
  // table & modal state
  const [data, setData] = useState<ArchivalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, _] = useState<ArchivalRequest | null>(null); //NOSONAR
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [usernames, setUsernames] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  // filter states (match ArchivalReportParams: from,to,dateType,status,controller,user)
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [dateType, setDateType] = useState<"request" | "decision">("request");
  const [status, setStatus] = useState<string>("");
  const [controller, setController] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>(""); // username or id search
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  // pagination (kept simple; server side pagination can be added)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);

  // supporting lists
  const [controllers, setControllers] = useState<any[]>([]);

  const fetchUsernames = async () => {
    try {
      const res = await getAllUsernames();
      if (res?.data?.data) {
        setUsernames(res.data.data);
      }
    } catch (err: any) {
      logger.error("Failed to fetch usernames", err);
    }
  };
  useEffect(() => {
    fetchControllers();
    fetchUsernames();
  }, []);

  const fetchControllers = async () => {
    try {
      const res = await getAllControllers();
      if (res?.data?.data) {
        setControllers(res.data.data);
      }
    } catch (err: any) {
      logger.error("Failed to get controllers", err);
    }
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchArchivalReport();
      setShouldFetch(false);
    }
  }, [shouldFetch]);
  useEffect(() => {
    fetchArchivalReport();
  }, [currentPage]);

  const handleDateInputValidation = (
    value: string,
    setter: (v: string) => void,
  ) => {
    if (!value) return setter("");

    const isoValue = toISOForDB(value) ?? "";

    if (setter === setFrom && to && new Date(isoValue) > new Date(to)) {
      setTo(isoValue);
    }

    if (setter === setTo && from && new Date(isoValue) < new Date(from)) {
      setFrom(isoValue);
    }

    setter(isoValue); // ✅ store ISO only
  };

  // Build params object for API
  const buildParams = () => {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (dateType) params.dateType = dateType;
    if (status) params.status = status;
    if (controller) params.controller = controller;
    if (userFilter) params.user = userFilter;
    params.page = currentPage;
    params.limit = pageSize;
    return params;
  };

  // main fetch
  const fetchArchivalReport = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const res = await getArchivalReport(params);

      if (res?.data?.status) {
        const mapped = (res.data.data || []).map((item: any) => ({
          ...item,
          controllerName: item.controllerUser
            ? `${item.controllerUser.firstName || ""} ${
                item.controllerUser.lastName || ""
              }`.trim() ||
              item.controllerUser.username ||
              "-"
            : "-",
        }));

        // Remove duplicates based on _id

        //  Set cleaned data and total pages
        setData(mapped);
        setTotalPages(res?.data?.meta?.totalPages || 1);
        setTotalCount(res?.data?.meta?.total || 0);
        setApprovedCount(res?.data?.meta?.approvedCount || 0);
        setPendingCount(res?.data?.meta?.pendingCount || 0);
        setRejectedCount(res?.data?.meta?.rejectedCount || 0);
      } else {
        toast.error(res?.data?.message || "Failed to fetch archival report");
      }
    } catch (err: any) {
      logger.error(err);
      toast.error(err?.message || "Error fetching archival report");
    } finally {
      setLoading(false);
      setShowExportDropdown(false);
    }
  };

  const handleFilter = async () => {
    try {
      setFilterLoading(true);

      setCurrentPage(1);

      await fetchArchivalReport();
    } catch (error: any) {
      logger.error("Filter error:", error);
      toast.error(error?.message || "Failed to fetch report");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetLoading(true);

      setFrom("");
      setTo("");
      setDateType("request");
      setStatus("");
      setController("");
      setUserFilter("");
      setShouldFetch(true);
    } catch (error: any) {
      logger.error("Reset error:", error);
      toast.error("Something went wrong while resetting");
    } finally {
      setResetLoading(false);
    }
  };

  // Export handler using your API helpers (blob)
  const handleExport = async (type: "csv" | "excel" | "pdf") => {
    try {
      setExportLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const params = buildParams();
      setShowExportDropdown(false); // close dropdown

      let apiCall: any;
      let blobType = "";
      let fileExt = "";

      if (type === "csv") {
        apiCall = exportArchivalReportCSV;
        blobType = "text/csv";
        fileExt = "csv";
      } else if (type === "excel") {
        apiCall = exportArchivalReportExcel;
        blobType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        fileExt = "xlsx";
      } else {
        apiCall = exportArchivalReportPDF;
        blobType = "application/pdf";
        fileExt = "pdf";
      }

      const res = await apiCall(params);

      if (res && (res.status === 200 || res.status === 201)) {
        const blob = new Blob([res.data], {
          type: blobType,
        });

        const filename = `Archival_Report_${
          new Date().toISOString().split("T")[0]
        }.${fileExt}`;
        const url = window.URL.createObjectURL(blob); //NOSONAR
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`${type.toUpperCase()} exported`);
      } else {
        toast.error("Export failed");
      }
    } catch (err: any) {
      logger.error(err);
      toast.error(err?.message || "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  // Approve / Reject actions (keeps your existing controller integration)
  const handleArchivalAction = async (
    request: ArchivalRequest,
    action: "Approve" | "Reject",
  ) => {
    try {
      const payload: any = {
        cnr: request.cnr,
        action,
        _id: request._id,
        reason:
          action === "Reject" ? request.reason || rejectReason : undefined,
      };
      const res = await archivalApprovedAndReject(payload);
      if (res?.data?.status) {
        toast.success(`Request ${action}ed`);
        await fetchArchivalReport();
        setModalOpen(false);
      } else {
        toast.error(res?.data?.message || "Action failed");
      }
    } catch (err: any) {
      logger.error(err);
      toast.error(err?.message || "Action failed");
    }
  };
  //renderTable
  const renderTableRows = () =>
    data.map((item, idx) => (
      <ArchivalTableRow
        key={item._id || idx}
        item={item}
        idx={idx}
        currentPage={currentPage}
        pageSize={pageSize}
      />
    ));

  const renderTableContent = () => {
    if (loading) {
      return renderTableMessage("Loading...");
    }

    if (data?.length > 0) {
      return renderTableRows();
    }

    return renderTableMessage("No records found");
  };

  const filterButtonIcon = renderButtonIcon(filterLoading, Filter);

  const exportButtonIcon = renderButtonIcon(exportLoading, Download);

  const filterButtonText = filterLoading ? "Applying..." : "Apply Filters";

  const exportButtonText = exportLoading ? "Exporting..." : "Export Report";

  const resetButtonClass = resetLoading
    ? "opacity-50 cursor-not-allowed"
    : "opacity-100 cursor-pointer";

  const resetIconClass = resetLoading
    ? "w-4 h-4 mr-2 animate-spin"
    : "w-4 h-4 mr-2";

  return (
    <div className="max-w-screen w-full min-h-screen">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        <div className="flex items-center gap-3 md:mb-6 mb-3">
          <div className="bg-white rounded-full p-2 shadow-sm  ">
            <Filter className="h-4 w-4 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Archival Report</h1>
        </div>
        <div className="bg-white rounded-xl  shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* From Date */}
            <div className="space-y-2">
              <p className="block text-sm font-medium text-gray-700">
                From Date
              </p>
              <DateInput
                value={formatDisplayDate(from)}
                onChange={(date: string) =>
                  handleDateInputValidation(date, setFrom)
                }
              />
            </div>

            {/* To Date */}
            <div className="space-y-2">
              <p className="block text-sm font-medium text-gray-700">To Date</p>
              <div className="relative">
                <DateInput
                  value={formatDisplayDate(to)}
                  onChange={(date: string) =>
                    handleDateInputValidation(date, setTo)
                  }
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <p className="block text-sm font-medium text-gray-700">Status</p>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                >
                  <option value="">All</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Controller */}
            <div className="space-y-2">
              <p className="block text-sm font-medium text-gray-700">
                Controller
              </p>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={controller}
                  onChange={(e) => setController(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                >
                  <option value="">All Controllers</option>
                  {controllers.map((ctrl: any) => (
                    <option key={ctrl.value} value={ctrl.value}>
                      {ctrl.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* User Filter (Dropdown) */}
            <div className="space-y-2">
              <p className="block text-sm font-medium text-gray-700">User</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                >
                  <option value="">All Users</option>
                  {usernames.map((u: any, i: number) => (
                    <option key={`${u.value}-${i}`} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-x-3">
              <button
                onClick={handleFilter}
                disabled={filterLoading}
                className="w-48 inline-flex items-center px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {filterButtonIcon}
                {filterButtonText}
              </button>

              <button
                onClick={handleReset}
                disabled={resetLoading}
                className={`sm:mt-0 mt-3 inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors ${resetButtonClass}`}
              >
                <RefreshCw className={resetIconClass} />
                Reset
              </button>
            </div>

            <div className="relative">
              <button
                type="button"
                disabled={exportLoading}
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="inline-flex items-center px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {exportButtonIcon}
                {exportButtonText}
              </button>

              {showExportDropdown && (
                <div className=" absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleExport("csv")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as CSV
                  </button>
                  <button
                    onClick={() => handleExport("excel")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as Excel
                  </button>
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {getSummaryCards(
            totalCount,
            approvedCount,
            pendingCount,
            rejectedCount,
          ).map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.title}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {card.title}
                    </p>

                    <p className={`text-3xl font-bold ${card.textColor}`}>
                      {card.count}
                    </p>
                  </div>

                  <div className={`p-3 rounded-lg ${card.bgColor}`}>
                    <Icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              View Requests
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S. No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requesting User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Request Date / Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Controller
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Action Date / Time
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th> */}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {renderTableContent()}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex justify-end items-center gap-4">
          {totalPages > 0 && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </div>
      </div>

      <ArchivalDetailsModal
        modalOpen={modalOpen}
        selectedRequest={selectedRequest}
        userRole={user?.role}
        showRejectReason={showRejectReason}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        setShowRejectReason={setShowRejectReason}
        setModalOpen={setModalOpen}
        handleArchivalAction={handleArchivalAction}
      />
      {/* Video modal */}
      {videoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6">
            <button
              className="absolute top-3 right-5 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              onClick={() => {
                setVideoModalOpen(false);
                setVideoUrl(null);
              }}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 className="text-xl font-semibold mb-4 text-blue-700">
              Archived Video
            </h2>

            {videoUrl ? (
              <video
                width="100%"
                height="auto"
                controls
                controlsList="nodownload"
              >
                <source src={videoUrl} type="video/mp4" />
                <track kind="captions" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <p className="text-gray-500">Loading video...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivalReport;
